import { type RefObject, useRef } from 'react';
import { Modal } from '@/app/components/Modal';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { useModalContentHeight } from '@/app/components/Modal/hooks/useModalContentHeight';
import { Spinner } from '@/app/components/Spinner';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { cn } from '@/utils/сn';
import type { ModalShellProps } from './types';

const MODAL_PANEL_MAX_HEIGHT = 'max-h-[min(800px,90vh)]';

export const ModalShell = ({
  isOpen,
  title,
  subtitle,
  onClose,
  onSubmit,
  isSubmitting = false,
  isLoading = false,
  canSubmit = true,
  submitLabel = 'Save changes',
  cancelLabel = 'Cancel',
  className = '',
  panelClassName = 'w-full max-w-[550px] md:min-w-[550px]',
  maxHeightClassName,
  children,
}: ModalShellProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { hasScrollableContent, setContentRef } = useModalContentHeight<HTMLElement>(
    isOpen,
    children,
    contentRef as RefObject<HTMLElement>
  );

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose} variant="fullScreenMobile">
      <div
        className={cn(
          'flex flex-col rounded-3xl bg-white w-full max-w-[550px] md:min-w-[550px] overflow-hidden pointer-events-auto',
          MODAL_PANEL_MAX_HEIGHT,
          'max-md:h-full max-md:max-h-none max-md:max-w-none max-md:rounded-none',
          panelClassName,
          className
        )}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex flex-col gap-4 items-center justify-between px-[36px] pt-[46px] max-md:items-stretch max-md:px-4 max-md:pt-4">
          <p className="text-blue-900 text-[28px] leading-[130%] font-light font-cenela max-md:hidden">{title}</p>
          <div className="hidden max-md:block">
            <ModalMobileHeader title={title} onBack={onClose} />
          </div>
          {subtitle ? (
            <p className="text-body-regular text-grey-700 text-center max-md:text-body-small max-md:text-left">
              {subtitle}
            </p>
          ) : null}
        </div>
        <div
          ref={setContentRef}
          className={cn(
            'flex-1 min-h-0 overflow-y-auto px-[36px] pt-[30px] w-full max-md:px-4 max-md:pt-5',
            maxHeightClassName
          )}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center" aria-label="Loading">
              <Spinner />
            </div>
          ) : (
            children
          )}
        </div>

        <div
          className={cn(
            'flex items-center justify-end gap-[20px] pt-[30px] mx-[36px] pb-[46px] bg-white',
            'max-md:mx-4 max-md:gap-3 max-md:pt-4 max-md:pb-6',
            maxHeightClassName,
            { 'border-t border-grey-400': hasScrollableContent && !isLoading }
          )}
        >
          <BaseButton
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            intent="secondary"
            size="full"
            className="max-w-[229px] max-md:max-w-none max-md:flex-1"
          >
            {cancelLabel}
          </BaseButton>

          {onSubmit ? (
            <BaseButton
              type="button"
              onClick={onSubmit}
              intent="primary"
              size="full"
              className="max-w-[229px] max-md:max-w-none max-md:flex-1"
              disabled={!canSubmit || isSubmitting || isLoading}
            >
              {isSubmitting ? 'Saving...' : submitLabel}
            </BaseButton>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
